import { FC, ReactNode, MouseEventHandler, useCallback } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';

import { Button } from 'dibs-elements/exports/Button';
import { addLocalStorageCartItem } from 'dibs-buyer-layout/src/actions/cartActions';

import { PdpSharedShoppingButton_user$key } from './__generated__/PdpSharedShoppingButton_user.graphql';
import { PdpSharedShoppingButton_item$key } from './__generated__/PdpSharedShoppingButton_item.graphql';

const userFragment = graphql`
    fragment PdpSharedShoppingButton_user on User {
        serviceId
    }
`;
const itemFragment = graphql`
    fragment PdpSharedShoppingButton_item on Item {
        serviceId @required(action: THROW)
        defaultSkuId @required(action: THROW)
    }
`;

const userCartItemAddMutation = graphql`
    mutation PdpSharedShoppingButton_userCartItemAddMutation($input: UserCartItemAddInput!) {
        userCartItemAdd(input: $input) {
            clientMutationId
        }
    }
`;

export const PdpSharedShoppingButton: FC<{
    user: PdpSharedShoppingButton_user$key | null;
    item: PdpSharedShoppingButton_item$key;
    skuId?: string;
    quantitySelected?: string;
    type?: 'primary' | 'secondary';
    onClick: MouseEventHandler;
    children: ReactNode;
    disabled?: boolean;
}> = ({
    user: userRef,
    item: itemRef,
    skuId,
    quantitySelected,
    type = 'primary',
    onClick,
    children,
    disabled,
}) => {
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);

    const [commitUserCartItemAdd, userCartItemAddInFlight] = useMutation(userCartItemAddMutation);

    const isUserLoggedIn = !!user?.serviceId;
    const { serviceId: itemId } = item;

    const handleClick: MouseEventHandler = useCallback(
        e => {
            if (isUserLoggedIn) {
                commitUserCartItemAdd({
                    variables: {
                        input: {
                            itemId,
                            skuId: skuId || item.defaultSkuId,
                            quantity: quantitySelected || '1',
                        },
                    },
                });
            } else {
                addLocalStorageCartItem(itemId);
            }

            onClick(e);
        },
        [
            isUserLoggedIn,
            itemId,
            skuId,
            quantitySelected,
            onClick,
            commitUserCartItemAdd,
            item.defaultSkuId,
        ]
    );

    return (
        <Button
            fullWidth
            type={type}
            size="large"
            onClick={handleClick}
            disabled={userCartItemAddInFlight || disabled}
        >
            {children}
        </Button>
    );
};
